import React, { useState, useRef } from 'react';
import {
  IonModal,
  IonContent,
  IonIcon,
  IonImg,
} from '@ionic/react';
import { arrowForward, close } from 'ionicons/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { customGradients } from '../theme/theme';

// Styled components
const SlideContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: theme.spacing(4),
  paddingBottom: theme.spacing(16), // Add extra padding at the bottom to prevent text from being hidden by the button container
  textAlign: 'center',
}));

const SlideImage = styled(Box)(({ theme }) => ({
  width: '80%',
  maxWidth: '300px',
  height: '200px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.8)',
  boxShadow: '0 8px 32px rgba(0, 104, 185, 0.1)',
  overflow: 'hidden',
  animation: 'float 6s ease-in-out infinite',
}));

const SlideTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.8rem',
  marginBottom: theme.spacing(2),
  background: customGradients.primary,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const SlideDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  maxWidth: '80%',
  margin: '0 auto',
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ProgressDot = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  margin: '0 5px',
  background: active ? customGradients.primary : theme.palette.grey[300],
  transition: 'all 0.3s ease',
  transform: active ? 'scale(1.2)' : 'scale(1)',
}));

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = 3;
  const swiperRef = useRef<any>(null);

  const handleSlideChange = (swiper: any) => {
    setActiveSlide(swiper.activeIndex);
  };

  const handleNext = () => {
    if (activeSlide < totalSlides - 1) {
      // Directly access the swiper instance and call slideNext
      if (swiperRef.current && swiperRef.current.swiper) {
        swiperRef.current.swiper.slideNext();
      }
    } else {
      onClose();
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="tutorial-modal">
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        zIndex: 1100 
      }}>
        <IconButton onClick={onClose} size="large" sx={{ color: 'text.secondary' }}>
          <IonIcon icon={close} />
        </IconButton>
      </Box>

      <IonContent>
        <Swiper
          initialSlide={0}
          speed={400}
          onSlideChange={handleSlideChange}
          ref={swiperRef}
          style={{ height: '100%' }}
        >
          {/* Slide 1: Discover */}
          <SwiperSlide>
            <SlideContainer>
              <SlideImage className="animate-float">
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IonImg
                    src="/assets/image/tutorial/discover.jpeg"
                    alt="Discover"
                    style={{
                      width: '70%',
                      height: '70%',
                      margin: 'auto'
                    }}
                  />
                </Box>
              </SlideImage>
              <SlideTitle variant="h2">{t('tutorial.discover_title')}</SlideTitle>
              <SlideDescription>
                {t('tutorial.discover_description')}
              </SlideDescription>
            </SlideContainer>
          </SwiperSlide>

          {/* Slide 2: Connect */}
          <SwiperSlide>
            <SlideContainer>
              <SlideImage className="animate-float">
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IonImg
                    src="/assets/image/tutorial/connect.jpeg"
                    alt="Connect"
                    style={{
                      width: '80%',
                      height: '80%',
                      objectFit: 'cover',
                      margin: 'auto'
                    }}
                  />
                </Box>
              </SlideImage>
              <SlideTitle variant="h2">{t('tutorial.connect_title')}</SlideTitle>
              <SlideDescription>
                {t('tutorial.connect_description')}
              </SlideDescription>
            </SlideContainer>
          </SwiperSlide>

          {/* Slide 3: Share */}
          <SwiperSlide>
            <SlideContainer>
              <SlideImage className="animate-float">
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IonImg
                    src="/assets/image/tutorial/share.jpeg"
                    alt="Share"
                    style={{
                      width: '80%',
                      height: '80%',
                      objectFit: 'cover',
                      margin: 'auto'
                    }}
                  />
                </Box>
              </SlideImage>
              <SlideTitle variant="h2">{t('tutorial.share_title')}</SlideTitle>
              <SlideDescription>
                {t('tutorial.share_description')}
              </SlideDescription>
            </SlideContainer>
          </SwiperSlide>
        </Swiper>

        <Box sx={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          padding: 3,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1000 // Add a high z-index to ensure it's above the Swiper
        }}>
          <ProgressIndicator>
            {[...Array(totalSlides)].map((_, index) => (
              <ProgressDot key={index} active={index === activeSlide} />
            ))}
          </ProgressIndicator>

          <Button
            variant="contained"
            size="large"
            endIcon={<IonIcon icon={arrowForward} />}
            onClick={handleNext}
            sx={{
              background: customGradients.primary,
              borderRadius: '12px',
              padding: '10px 24px',
              fontWeight: 600,
              width: '100%',
              maxWidth: '300px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0, 104, 185, 0.3)',
              },
            }}
          >
            {activeSlide === totalSlides - 1 ? t('tutorial.get_started') : t('tutorial.next')}
          </Button>
        </Box>
      </IonContent>
    </IonModal>
  );
};
export default TutorialModal;
